import { Alert, Currency, DatePickerPopover, InlineValuePopover, Input } from "@xola/ui-kit";
import {
    DateFormat,
    formatDate,
    FormField,
    isExperienceProduct,
    isMerchandiseProduct,
    isPackageProduct,
    isPartnerPackageProduct,
    now,
} from "common";
import dayjs, { Dayjs } from "dayjs";
import { useField } from "formik";
import { find, minBy, round } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { PaymentMethod, PaymentReminderType } from "types";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";
import { hasDeposit, isPackagePurchase } from "../../models/Purchase";
import { getItemProduct, getPurchasePackageDetails, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { SuffixField } from "../Field/SuffixField";
import { calculateDeposit } from "@/models/DownDeposit";
import { isSharedDemographicsPackage } from "@/models/Package";

export const CreatePurchaseDepositFields = () => {
    const {
        purchase,
        item: { balanceDueOffset },
        activeItemIndex,
    } = useCreatePurchaseStore((state) => state.getPurchaseAndActiveItem());
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const [dueNow, amount = 0] = useCreatePurchaseStore((state) => [state.dueNow, state.amount], shallow);

    const earliestArrivalDateTime = useCreatePurchaseStore((state) => state.getEarliestArrivalDateTime());
    const arrivalDate = earliestArrivalDateTime;
    const paymentLineItem = useCreatePurchaseStore((state) => state.paymentLineItem);

    const [days, setDays] = useState(balanceDueOffset ?? 0);
    const [daysPopover, setIsDaysPopoverOpen] = useState(false);
    const [depositDate, setDepositDate] = useState(now(arrivalDate).subtract(days, "days"));
    const [isDepositManuallyEdited, setIsDepositManuallyEdited] = useState(false);

    useEffect(() => {
        if (days === 0 && isPackagePurchase(purchase)) {
            const item = minBy(purchase.items, "arrivalDate");
            if (item) {
                setDepositDate(now(item.arrivalDate).subtract(days, "days"));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchase]);

    const reminder = find(purchase.paymentReminders, { type: PaymentReminderType.SELLER_DEPOSIT });
    const reminderDate = reminder?.dueAt ? reminder.dueAt : "N/A";
    // const reminderAmount = reminder?.amount ?? 0;
    // const [debouncedReminderAmount] = useDebounce(reminderAmount, 300);

    const depositAmount = Number.isNaN(purchase.payNow) || !purchase.payNow ? 0 : purchase.payNow;
    const [debouncedDepositAmount] = useDebounce(depositAmount, 100);

    const depositHelper = useField("deposit");
    const depositDurationHelper = useField("depositDuration");

    const isDeposit = hasDeposit(purchase);
    const isInvoice = paymentLineItem.payment.method === PaymentMethod.INVOICE;

    const dateFormat = DateFormat.DATE_LONG;

    const depositValue = round(depositHelper[0].value, 2);
    const purchaseAmount = round(purchase.amount, 2);

    const hasExperienceItems = useCreatePurchaseStore((state) => state.hasExperienceItems());
    // Calculate total deposit for all items in MIST booking or package purchase
    const calculatedDeposit = useMemo(() => {
        if (!isDeposit || !amount) return 0;

        let totalDeposit = 0;
        let hasCalculatedOutingDeposit = false;

        for (const item of purchase.items) {
            const product = isPackagePurchase(purchase) ? getPurchasePackageDetails(item) : getItemProduct(item);
            const itemAmount = item.amount ?? 0;
            const isDownDepositAllowed =
                isExperienceProduct(product) ||
                isPackageProduct(product) ||
                isPartnerPackageProduct(product) ||
                isMerchandiseProduct(product);

            if (isDownDepositAllowed) {
                // Check if this is a package with outing scope
                const isPackageWithOutingScope =
                    (isPackageProduct(product) || isPartnerPackageProduct(product)) &&
                    product.downDeposit?.scope === "outing";

                // Check if package is for common demographic and has per person pricing
                const isSharedDemographicPerPersonPackage =
                    isPackageProduct(product) &&
                    isSharedDemographicsPackage(product) &&
                    product.downDeposit?.scope === "person";

                const shouldCalculateOnce = isPackageWithOutingScope || isSharedDemographicPerPersonPackage;

                if (shouldCalculateOnce && hasCalculatedOutingDeposit) {
                    continue;
                }

                const itemDeposit = calculateDeposit(
                    product,
                    { ...purchase, amount: shouldCalculateOnce ? amount : itemAmount },
                    item,
                );
                totalDeposit += itemDeposit;

                if (shouldCalculateOnce) {
                    hasCalculatedOutingDeposit = true;
                }
            }
        }

        return totalDeposit;
    }, [isDeposit, amount, purchase]);

    const depositHelpers = depositHelper[2];
    const currentDepositValue = depositHelper[0].value;
    const shouldSetDeposit =
        !isDepositManuallyEdited &&
        (currentDepositValue !== calculatedDeposit || currentDepositValue === 0) &&
        calculatedDeposit > 0;

    useEffect(() => {
        if (!shouldSetDeposit) return;
        depositHelpers.setValue(calculatedDeposit);
    }, [shouldSetDeposit, calculatedDeposit, depositHelpers]);

    useEffect(() => {
        const arrivalOffset = balanceDueOffset ?? 0;

        depositDurationHelper[2].setValue(arrivalOffset);
        setPurchase((draft) => {
            draft.paymentReminders = draft.paymentReminders?.map((reminder) => {
                if (depositReminderTypes.has(reminder.type)) {
                    reminder.dueAt = earliestArrivalDateTime?.subtract(arrivalOffset, "day");
                    reminder.arrivalOffset = dayjs.duration(arrivalOffset, "days").asMinutes();
                }

                return reminder;
            });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentLineItem.payment.method, isDeposit]);

    useEffect(() => {
        setPurchase((draft) => {
            if (draft.paymentReminders?.length) {
                if (isInvoice && isDeposit) {
                    draft.paymentReminders = draft.paymentReminders.map((reminder) => {
                        if (invoiceReminderTypes.has(reminder.type)) {
                            reminder.amount = depositValue;
                        }

                        return reminder;
                    });
                }

                draft.payNow = depositValue;
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depositValue, isInvoice, purchaseAmount, purchase.paymentReminders?.length]);

    const updateTiming = (days: number) => {
        const depositDays = Number(days ?? 0);
        setDays(depositDays);
        setDepositDate(now(arrivalDate).subtract(depositDays, "days"));
        depositDurationHelper[2].setValue(depositDays);

        setPurchase((draft) => {
            draft.items[activeItemIndex].balanceDueOffset = depositDays;
            draft.paymentReminders = draft.paymentReminders?.map((reminder) => {
                if (depositReminderTypes.has(reminder.type)) {
                    reminder.dueAt = earliestArrivalDateTime.subtract(depositDays, "day");
                    reminder.arrivalOffset = dayjs.duration(depositDays, "days").asMinutes();
                }

                return reminder;
            });
        });
    };

    /**
     * Handles the change of the deposit date.
     *
     * @param value - The new date value form datepicker or from the state when the sected date is updated, use with the second param as true in that case.
     * @param keepOffset - A boolean indicating whether to keep the offset. Defaults to false. Is used to keep offset when the arrival date is changed.
     */

    const handleDepositDateChange = (value: Date, keepOffset = false) => {
        const shouldApplyOffset = keepOffset && balanceDueOffset;
        const selectedDate = shouldApplyOffset ? now(value).add(-balanceDueOffset, "days") : now(value);
        const daysDiff = now(arrivalDate).startOf("day").diff(selectedDate.startOf("day"), "days");

        setDepositDate(selectedDate);
        updateTiming(daysDiff);
    };

    useEffect(() => {
        if (depositDate.add(days, "days").isSame(arrivalDate)) {
            /**
             * If addition of (deposit due date + offset days) is same as the `arrivalDate`, means arrivalDate is not changed yet.
             * The `useEffect` execution may be happened cuz of initial render of this component in MIST flow, We don't want to do anything.
             **/
            return;
        }

        /** When the arrival date for the purchase is changed, we also need to update the deposit due date value  */
        // Added `keepOffset` to keep the offset days same when the arrival date is changed as the handler would compare two instances of the same arrival date and reset the offset days otherwise X2-9118
        handleDepositDateChange(arrivalDate.toDate(), true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- We need to execute this only on `arrivalDate` change
    }, [arrivalDate.toString()]);

    return (
        <>
            <FormField prevalidate className="order-0" name="deposit" label="Deposit">
                <SuffixField suffix="Payable now">
                    <CurrencyInputField
                        filterZero
                        className="rounded-r-none border-r-0 hover:border-gray-light focus:border-gray-light"
                        onFocus={() => setIsDepositManuallyEdited(true)}
                    />
                </SuffixField>
            </FormField>
            {hasExperienceItems && (
                <FormField prevalidate className="order-2" name="depositDuration" label="Balance">
                    <Currency currency={purchase.currency}>{amount - debouncedDepositAmount}</Currency> due{" "}
                    <InlineValuePopover
                        showArrow
                        text={days}
                        isOpen={daysPopover}
                        onClick={() => setIsDaysPopoverOpen(!daysPopover)}
                        onClickOutside={() => setIsDaysPopoverOpen(!daysPopover)}
                    >
                        <Input
                            value={days ? days : ""}
                            type="number"
                            min="0"
                            step="1"
                            onChange={(e: any) => updateTiming(e.target.value)}
                        />
                    </InlineValuePopover>
                    day(s) before event
                    <DatePickerPopover
                        popoverProps={{ placement: "auto" }}
                        value={depositDate.toDate()}
                        disabledDays={[
                            {
                                after: now(arrivalDate).toDate(),
                                before: now().toDate(),
                            },
                        ]}
                        onChange={(value: Date) => handleDepositDateChange(value)}
                    >
                        <span data-testid="balance-due-on">
                            &nbsp; (due on&nbsp;
                            <NoTranslate as="strong" className="underline hover:cursor-pointer">
                                {formatDate(depositDate, DateFormat.DATE_LONG)}
                            </NoTranslate>
                            )
                        </span>
                    </DatePickerPopover>
                </FormField>
            )}
            {depositAmount > 0 && paymentLineItem.payment.method !== PaymentMethod.INVOICE && (
                <Alert className="order-last">
                    <div className="space-y-2">
                        <h4 className="font-bold">Payment Timeline Summary</h4>
                        <div data-testid="create-purchase-deposit-fields-due-now">
                            <Currency currency={purchase.currency}>{dueNow}</Currency> due now
                        </div>
                        <div data-testid="create-purchase-deposit-fields-due">
                            <Currency currency={purchase.currency}>{amount - depositAmount}</Currency> due
                            <NoTranslate className="mx-1 font-bold">
                                {formatDate(reminderDate as Dayjs, dateFormat)}
                            </NoTranslate>
                        </div>
                    </div>
                </Alert>
            )}
        </>
    );
};

const depositReminderTypes = new Set([PaymentReminderType.SELLER_DEPOSIT, PaymentReminderType.BALANCE_DUE_TRAVELER]);
const invoiceReminderTypes = new Set([PaymentReminderType.INVOICE_SELLER, PaymentReminderType.INVOICE_TRAVELER]);
